import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import onrampService from '../services/onrampService.js';
import supportedService from '../services/supportedService.js';
import walletPurchaseService from '../services/walletPurchaseService.js';
import { getTranslation } from '../i18n/translations.js';
import './BuyCrypto.css';

// Country flag emoji mapping for common currencies
const CURRENCY_FLAGS = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', INR: '🇮🇳', AUD: '🇦🇺',
  CAD: '🇨🇦', JPY: '🇯🇵', CHF: '🇨🇭', SGD: '🇸🇬', HKD: '🇭🇰',
  NZD: '🇳🇿', SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', PLN: '🇵🇱',
  BRL: '🇧🇷', MXN: '🇲🇽', ZAR: '🇿🇦', AED: '🇦🇪', THB: '🇹🇭',
  PHP: '🇵🇭', MYR: '🇲🇾', IDR: '🇮🇩', VND: '🇻🇳', KRW: '🇰🇷',
  TRY: '🇹🇷', RUB: '🇷🇺', CZK: '🇨🇿', HUF: '🇭🇺', ILS: '🇮🇱',
};

// Crypto icon colors (ensure sufficient contrast with white text)
const CRYPTO_COLORS = {
  ETH: '#627eea', BTC: '#e8860c', USDC: '#2775ca', USDT: '#26a17b',
  MATIC: '#8247e5', BNB: '#c99400', SOL: '#9945ff', AVAX: '#e84142',
  ARB: '#28a0f0', OP: '#ff0420', BASE: '#0052ff', TRX: '#eb0029',
  tBNB: '#c99400', DAI: '#f5ac37',
};

// Network icon colors (ensure sufficient contrast with white text)
const NETWORK_COLORS = {
  ethereum: '#627eea',
  arbitrum: '#28a0f0',
  polygon: '#8247e5',
  optimism: '#ff0420',
  base: '#0052ff',
  bsc: '#c99400',
  bsc_testnet: '#c99400',
  avalanche: '#e84142',
  solana: '#9945ff',
  tron: '#eb0029',
  bitcoin: '#e8860c',
};

// Provider logo URLs (fallback, will be overridden by API data)
const PROVIDER_LOGOS = {
  transak: 'https://cdn.onramper.com/icons/onramps/transak-colored.svg',
  banxa: 'https://cdn.onramper.com/icons/onramps/banxa-colored.svg',
  'onramp-money': 'https://onramp.money/images/onramp-logo.svg',
  onmeta: 'https://onmeta.in/logo.svg',
  guardarian: 'https://guardarian.com/main-logo-dark.png',
  onramper: 'https://cdn.onramper.com/icons/onramps/onramper-colored.svg',
};

// Currency-specific minimum and maximum amounts (approximate USD equivalents)
// MIN ~ $30 USD equivalent, MAX ~ $50,000 USD equivalent
// These are conservative frontend guards; providers enforce their own exact limits
const CURRENCY_LIMITS = {
  USD: { min: 30, max: 50000 },
  EUR: { min: 28, max: 46000 },
  GBP: { min: 24, max: 40000 },
  INR: { min: 2500, max: 4200000 },
  CAD: { min: 42, max: 69000 },
  AUD: { min: 46, max: 77000 },
  JPY: { min: 4500, max: 7500000 },
  CHF: { min: 27, max: 45000 },
  SGD: { min: 40, max: 67000 },
  HKD: { min: 235, max: 390000 },
  NZD: { min: 50, max: 83000 },
  SEK: { min: 320, max: 530000 },
  NOK: { min: 330, max: 550000 },
  DKK: { min: 210, max: 345000 },
  PLN: { min: 120, max: 200000 },
  BRL: { min: 150, max: 250000 },
  MXN: { min: 520, max: 860000 },
  ZAR: { min: 550, max: 910000 },
  AED: { min: 110, max: 184000 },
  THB: { min: 1050, max: 1750000 },
  PHP: { min: 1700, max: 2800000 },
  MYR: { min: 140, max: 230000 },
  IDR: { min: 480000, max: 790000000 },
  VND: { min: 750000, max: 1250000000 },
  KRW: { min: 40000, max: 66000000 },
  TRY: { min: 970, max: 1600000 },
  CZK: { min: 700, max: 1150000 },
  HUF: { min: 11000, max: 18500000 },
  ILS: { min: 110, max: 183000 },
};
const DEFAULT_CURRENCY_LIMITS = { min: 30, max: 50000 };

// Currency symbols for display in error messages
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$',
  CAD: 'C$', JPY: '¥', CHF: 'CHF ', SGD: 'S$', HKD: 'HK$',
  AED: 'د.إ', THB: '฿', PHP: '₱', MYR: 'RM ', KRW: '₩',
  BRL: 'R$', MXN: 'MX$', IDR: 'Rp ', VND: '₫', NZD: 'NZ$',
  SEK: 'kr ', NOK: 'kr ', DKK: 'kr ', PLN: 'zł', ZAR: 'R ',
  TRY: '₺', CZK: 'Kč ', HUF: 'Ft ', ILS: '₪',
};

/**
 * BuyCrypto Component
 *
 * Main widget for buying cryptocurrency via on-ramp providers.
 *
 * Props:
 * @param {Object} config - Configuration options
 * @param {string} config.defaultAmount - Default fiat amount
 * @param {string} config.defaultFiat - Default fiat currency (USD, EUR, etc.)
 * @param {string} config.defaultCrypto - Default cryptocurrency (USDT, ETH, etc.)
 * @param {string} config.defaultNetwork - Default blockchain network
 * @param {string} config.walletAddress - Pre-filled wallet address
 * @param {boolean} config.isLocked - If true, amount/currency/crypto/network are read-only (for payment links)
 * @param {Object} config.lockedSession - Session data when isLocked is true
 * @param {Function} config.onCheckout - Custom checkout handler (for payment link flow)
 * @param {Object} bridge - PostMessage bridge for iframe communication
 * @param {boolean} isEmbedded - Whether widget is embedded in iframe
 */
const BuyCrypto = ({ config = {}, bridge = null, isEmbedded = false, language = null }) => {
  const [loading, setLoading] = useState(false);

  // Multi-language translation helper
  const activeLanguage = language || config.language || 'en';
  const t = useCallback((key, params) => getTranslation(activeLanguage, key, params), [activeLanguage]);

  // Locked mode flag - when true, amount/currency/crypto/network cannot be changed
  // This is used when the widget is opened from a secure payment link
  const isLocked = config.isLocked || false;

  // Per-field lock flags (for exchange/wallet iframe integration via URL params)
  // Wallet is ALWAYS disabled in the widget — must be passed via URL (?wallet=0x...).
  // Without a wallet, the field stays empty + disabled and the Buy button shows
  // "Connect Wallet to Continue" prompting the integrator to provide it.
  const isWalletLocked = false;
  const isAmountLocked = isLocked || !!config.lockAmount;
  const isFiatLocked = isLocked || !!config.lockFiat;
  const isCryptoLocked = isLocked || !!config.lockCrypto;

  // Form state - use config defaults if provided
  // When isLocked=true, these values come from the payment session and cannot be modified
  const [transactionType, setTransactionType] = useState(
    (config.type || '').toUpperCase() === 'SELL' ? 'SELL' : 'BUY'
  );
  const [fiatAmount, setFiatAmount] = useState(config.defaultAmount || '');
  const [fiatCurrency, setFiatCurrency] = useState(config.defaultFiat || 'EUR');
  const [cryptoCurrency, setCryptoCurrency] = useState(config.defaultCrypto || 'USDT');
  const [network, setNetwork] = useState(config.defaultNetwork || 'arbitrum');
  const [paymentMethod, setPaymentMethod] = useState('');
  const paymentMethodManuallySelected = useRef(false);
  const [walletAddress, setWalletAddress] = useState(config.walletAddress || '');
  const [walletError, setWalletError] = useState(null);

  // Sync config type & wallet dynamically when parent props/URL change
  useEffect(() => {
    if (config.type) {
      setTransactionType(config.type.toUpperCase() === 'SELL' ? 'SELL' : 'BUY');
    }
  }, [config.type]);

  useEffect(() => {
    if (config.walletAddress) {
      setWalletAddress(config.walletAddress);
      setWalletError(null);
    }
  }, [config.walletAddress]);

  // Amount validation
  const [amountError, setAmountError] = useState('');

  // Options loaded from API
  const [fiatCurrencies, setFiatCurrencies] = useState([]);
  const [cryptoCurrencies, setCryptoCurrencies] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Provider icons from API (keyed by provider name)
  const [providerIcons, setProviderIcons] = useState({});

  // Helper to get crypto icon from loaded data
  const getCryptoIcon = useCallback((code, networkName) => {
    const crypto = cryptoCurrencies.find(
      c => c.code === code && (!networkName || c.network === networkName)
    );
    return crypto?.icon || null;
  }, [cryptoCurrencies]);

  // Helper to get network icon from loaded data
  const getNetworkIcon = useCallback((networkName) => {
    const net = networks.find(n => n.name === networkName);
    return net?.icon || null;
  }, [networks]);

  // Provider quotes
  const [providerQuotes, setProviderQuotes] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [providerErrors, setProviderErrors] = useState({});

  // Quote staleness tracking
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isStale, setIsStale] = useState(false);

  // AbortController ref for cleanup
  const abortControllerRef = useRef(null);

  // Force refetch counter (incremented on amount input to handle same-value re-entry)
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Provider selection modal
  const [showProvidersModal, setShowProvidersModal] = useState(false);
  const [manualProvider, setManualProvider] = useState(null);
  const manualProviderSelectedRef = useRef(null);
  manualProviderSelectedRef.current = manualProvider;

  // Dropdown states
  const [showFiatDropdown, setShowFiatDropdown] = useState(false);
  const [showCryptoDropdown, setShowCryptoDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(null);
  const [cryptoSearch, setCryptoSearch] = useState('');
  // Two-step crypto dropdown: 'tokens' (step 1) or 'network' (step 2 — pick chain for multi-network token)
  const [cryptoDropdownStep, setCryptoDropdownStep] = useState('tokens');
  // Token group pending network selection (set when user picks a multi-network token in step 1)
  const [pendingTokenGroup, setPendingTokenGroup] = useState(null);

  // Refs for click outside
  const fiatDropdownRef = useRef(null);
  const cryptoDropdownRef = useRef(null);
  const paymentDropdownRef = useRef(null);

  // Screen reader announcement
  const [announcement, setAnnouncement] = useState('');

  // Wallet mode purchase tracking
  const isWalletMode = config.mode === 'wallet';
  const [activePurchaseId, setActivePurchaseId] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null);

  // Disclaimer consent
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Normal mode: awaiting payment detection (popup → success page → BroadcastChannel)
  const [awaitingPayment, setAwaitingPayment] = useState({
    active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null,
  });
  const popupWindowRef = useRef(null);
  const [paymentComplete, setPaymentComplete] = useState(null);

  // Helper to safely extract array from API response
  const extractArray = (value, ...keys) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') {
      for (const key of keys) {
        if (Array.isArray(value[key])) return value[key];
      }
    }
    return [];
  };

  // Fallback data
  const fallbackFiats = [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  ];

  // Fallback crypto+network combinations (each is a selectable item)
  const fallbackCryptoNetworks = [
    { code: 'USDT', name: 'Tether', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'USDT', name: 'Tether', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'USDT', name: 'Tether', network: 'polygon', networkName: 'Polygon' },
    { code: 'USDT', name: 'Tether', network: 'solana', networkName: 'Solana' },
    { code: 'USDC', name: 'USD Coin', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'USDC', name: 'USD Coin', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'USDC', name: 'USD Coin', network: 'polygon', networkName: 'Polygon' },
    { code: 'USDC', name: 'USD Coin', network: 'base', networkName: 'Base' },
    { code: 'USDC', name: 'USD Coin', network: 'solana', networkName: 'Solana' },
    { code: 'ETH', name: 'Ethereum', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'ETH', name: 'Ethereum', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'ETH', name: 'Ethereum', network: 'optimism', networkName: 'Optimism' },
    { code: 'ETH', name: 'Ethereum', network: 'base', networkName: 'Base' },
    { code: 'BTC', name: 'Bitcoin', network: 'bitcoin', networkName: 'Bitcoin' },
    { code: 'SOL', name: 'Solana', network: 'solana', networkName: 'Solana' },
    { code: 'MATIC', name: 'Polygon', network: 'polygon', networkName: 'Polygon' },
  ];

  // Legacy fallback for backward compatibility
  const fallbackCryptos = fallbackCryptoNetworks;

  const fallbackNetworks = [
    { name: 'ethereum', displayName: 'Ethereum' },
    { name: 'arbitrum', displayName: 'Arbitrum' },
    { name: 'polygon', displayName: 'Polygon' },
    { name: 'optimism', displayName: 'Optimism' },
    { name: 'base', displayName: 'Base' },
  ];

  // Use unified payment method IDs that work with both Transak and Banxa
  const fallbackMethods = [
    { id: 'card', name: 'Credit/Debit Card', recommended: true },
    { id: 'bank_transfer', name: 'Bank Transfer' },
    { id: 'apple_pay', name: 'Apple Pay' },
    { id: 'google_pay', name: 'Google Pay' },
  ];

  // Group tokens by symbol for two-step selection (Token → Network)
  // Each group: { symbol, name, icon, networks: [{ network, networkName, ...tokenData }] }
  const tokenGroups = useMemo(() => {
    const groups = new Map();
    const networkLocked = config.networkFilter; // URL ?network= param locks to one network

    cryptoCurrencies.forEach(c => {
      const code = c.symbol || c.code || c.ticker || '';
      if (!code) return;
      // When network is locked via URL, only include tokens on that network
      if (networkLocked && (c.network || '').toLowerCase() !== networkLocked.toLowerCase()) return;

      if (!groups.has(code)) {
        groups.set(code, {
          symbol: code,
          name: c.displayName || c.name || code,
          icon: c.icon || null,
          networks: [],
        });
      }
      const group = groups.get(code);
      // Avoid duplicate networks for the same token (case-insensitive comparison)
      const netKey = (c.network || '').toLowerCase();
      if (!group.networks.find(n => (n.network || '').toLowerCase() === netKey)) {
        group.networks.push({
          network: c.network || '',
          networkName: c.networkName || (c.network ? c.network.charAt(0).toUpperCase() + c.network.slice(1) : ''),
          icon: c.icon,
          ...c,
        });
      }
    });

    // Sort: popular tokens first (USDT, USDC, ETH, BTC...), then alphabetically
    const POPULAR = ['USDT', 'USDC', 'ETH', 'BTC', 'BNB', 'SOL', 'MATIC', 'TRX', 'DAI', 'POL'];
    return Array.from(groups.values()).sort((a, b) => {
      const ai = POPULAR.indexOf(a.symbol);
      const bi = POPULAR.indexOf(b.symbol);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.symbol.localeCompare(b.symbol);
    });
  }, [cryptoCurrencies, config.networkFilter]);

  // Helper: get currency limits (min/max) for the current fiat currency
  const getCurrencyLimits = useCallback((currencyCode) => {
    return CURRENCY_LIMITS[currencyCode] || DEFAULT_CURRENCY_LIMITS;
  }, []);

  // Helper: get currency symbol for display
  const getCurrencySymbol = useCallback((currencyCode) => {
    const fiatObj = fiatCurrencies.find(f => (f.code || f) === currencyCode);
    if (fiatObj?.symbol) return fiatObj.symbol;
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode + ' ';
  }, [fiatCurrencies]);

  // Amount validation function (dynamic per currency)
  const validateAmount = useCallback((value, currencyCode = fiatCurrency) => {
    const num = parseFloat(value);
    if (!value || String(value).trim() === '') {
      setAmountError('');
      return false;
    }
    if (isNaN(num) || num <= 0) {
      setAmountError('Please enter a valid amount');
      return false;
    }
    if (String(value).length > 15) {
      setAmountError('Maximum amount length is 15 digits');
      return false;
    }

    if (transactionType === 'SELL') {
      setAmountError('');
      return true;
    }

    const limits = getCurrencyLimits(currencyCode);
    const symbol = getCurrencySymbol(currencyCode);

    if (num < limits.min) {
      setAmountError(`Minimum amount is ${symbol}${limits.min.toLocaleString()}`);
      return false;
    }
    if (num > limits.max) {
      setAmountError(`Maximum amount is ${symbol}${limits.max.toLocaleString()}`);
      return false;
    }
    setAmountError('');
    return true;
  }, [fiatCurrency, transactionType, getCurrencyLimits, getCurrencySymbol]);

  // Re-validate amount when currency changes (minimum/maximum thresholds differ per currency)
  useEffect(() => {
    if (fiatAmount) {
      validateAmount(fiatAmount);
    }
  }, [fiatCurrency, fiatAmount, validateAmount]);

  // Automatically clear persistent wallet & widget errors when user modifies form inputs or provider selection
  useEffect(() => {
    setWalletError(null);
  }, [fiatCurrency, cryptoCurrency, network, paymentMethod, fiatAmount, selectedProvider]);

  // Quote staleness checker
  useEffect(() => {
    if (!lastFetchTime) return;
    const interval = setInterval(() => {
      const staleAfterMs = 60000; // 60 seconds
      setIsStale(Date.now() - lastFetchTime > staleAfterMs);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastFetchTime]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fiatDropdownRef.current && !fiatDropdownRef.current.contains(e.target)) {
        setShowFiatDropdown(false);
      }
      if (cryptoDropdownRef.current && !cryptoDropdownRef.current.contains(e.target)) {
        setShowCryptoDropdown(false);
      }
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(e.target)) {
        setShowPaymentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch options from API (using new supportedService with fallback to old onrampService)
  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        // Try new supported API first, fallback to old onrampService
        const [
          fiatResult,
          cryptoResult,
          networksResult,
          paymentResult,
          onrampsResult
        ] = await Promise.allSettled([
          supportedService.getFiatCurrencies(),
          supportedService.getTokens(),
          supportedService.getNetworks(),
          supportedService.getPaymentMethods(config.defaultFiat || 'EUR'),
          supportedService.getAllOnramps()
        ]);

        // Process fiat currencies from new API
        let fiats = fallbackFiats;
        if (fiatResult.status === 'fulfilled' && fiatResult.value) {
          const fiatData = Array.isArray(fiatResult.value) ? fiatResult.value : [];
          if (fiatData.length > 0) {
            fiats = fiatData.map(f => ({
              code: f.code,
              name: f.name,
              symbol: f.symbol,
              icon: f.icon
            }));
          }
        }
        setFiatCurrencies(fiats);

        // Process tokens from new API - already in crypto+network format
        let cryptos = fallbackCryptos;
        if (cryptoResult.status === 'fulfilled' && cryptoResult.value) {
          const tokenData = Array.isArray(cryptoResult.value) ? cryptoResult.value : [];
          if (tokenData.length > 0) {
            cryptos = tokenData.map(token => ({
              code: token.symbol,
              name: token.name,
              network: token.network,
              networkName: token.networkDisplayName || token.network,
              icon: token.icon,
              isStablecoin: token.isStablecoin
            }));
          }
        }
        console.log('Loaded cryptoCurrencies:', cryptos.slice(0, 5));
        setCryptoCurrencies(cryptos);

        // Process networks from new API
        let nets = fallbackNetworks;
        if (networksResult.status === 'fulfilled' && networksResult.value) {
          const netData = Array.isArray(networksResult.value) ? networksResult.value : [];
          if (netData.length > 0) {
            nets = netData.map(n => ({
              name: n.name,
              displayName: n.displayName,
              chainId: n.chainId,
              icon: n.icon
            }));
          }
        }
        setNetworks(nets);

        // Process payment methods from new API
        let methods = fallbackMethods;
        if (paymentResult.status === 'fulfilled' && paymentResult.value) {
          const methodData = Array.isArray(paymentResult.value) ? paymentResult.value : [];
          if (methodData.length > 0) {
            methods = methodData.map((m, idx) => ({
              id: m.paymentTypeId || m.id,
              code: m.paymentTypeId || m.id,
              name: m.name,
              icon: m.icon,
              recommended: idx === 0 // First is highest priority
            }));
          }
        }
        setPaymentMethods(methods);

        // Set default payment method (prefer config param, then recommended, then first)
        if (config.paymentMethod) {
          const configMethod = methods.find(m => (m.id || m.code) === config.paymentMethod);
          if (configMethod) {
            setPaymentMethod(configMethod.id || configMethod.code);
          } else {
            setPaymentMethod(config.paymentMethod);
          }
        } else {
          const defaultMethod = methods.find(m => m.recommended) || methods[0];
          if (defaultMethod) {
            setPaymentMethod(defaultMethod.id || defaultMethod.code || 'credit_debit_card');
          }
        }

        // Process onramp provider icons
        if (onrampsResult.status === 'fulfilled' && onrampsResult.value) {
          const onrampData = Array.isArray(onrampsResult.value) ? onrampsResult.value : [];
          const icons = {};
          onrampData.forEach(onramp => {
            icons[onramp.id] = onramp.icon || onramp.icons?.svg;
          });
          setProviderIcons(icons);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
        // Fall back to hardcoded data
        setFiatCurrencies(fallbackFiats);
        setCryptoCurrencies(fallbackCryptos);
        setNetworks(fallbackNetworks);
        setPaymentMethods(fallbackMethods);
        setPaymentMethod('card');
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Re-fetch payment methods and tokens when fiat currency changes
  // Tokens are filtered by eligible providers for the selected fiat
  useEffect(() => {
    if (!fiatCurrency) return;
    const fetchFilteredData = async () => {
      paymentMethodManuallySelected.current = false;
      try {
        // Fetch payment methods and tokens filtered by fiat in parallel
        const [methodData, tokenData] = await Promise.allSettled([
          supportedService.getPaymentMethods(fiatCurrency),
          supportedService.getTokens(undefined, fiatCurrency),
        ]);

        // Update payment methods
        if (methodData.status === 'fulfilled' && Array.isArray(methodData.value) && methodData.value.length > 0) {
          const methods = methodData.value.map((m, idx) => ({
            id: m.paymentTypeId || m.id,
            code: m.paymentTypeId || m.id,
            name: m.name,
            icon: m.icon,
            recommended: idx === 0,
          }));
          setPaymentMethods(methods);
          // Always auto-select first fiat-specific method on fiat change
          setPaymentMethod(methods[0]?.id);
        }

        // Update tokens/cryptos if filtered data is available
        if (tokenData.status === 'fulfilled' && Array.isArray(tokenData.value) && tokenData.value.length > 0) {
          const tokens = tokenData.value;
          setCryptoCurrencies(tokens);
          // Auto-select first crypto if current selection is no longer available
          const currentCryptoAvailable = tokens.some(
            (t) => (t.symbol || t.code) === cryptoCurrency
          );
          if (!currentCryptoAvailable && tokens.length > 0) {
            setCryptoCurrency(tokens[0].symbol || tokens[0].code);
            if (tokens[0].network) {
              setNetwork(tokens[0].network);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to filter data by fiat:', err);
      }
    };
    fetchFilteredData();
  }, [fiatCurrency]); // intentionally excludes paymentMethod/cryptoCurrency to avoid re-trigger loops

  // Ref to track current selected provider without causing re-renders
  const selectedProviderRef = useRef(null);
  selectedProviderRef.current = selectedProvider;

  // Fetch quotes from all providers using single API call
  const fetchQuotes = useCallback(async (signal) => {
    const isSell = transactionType === 'SELL';
    const amount = parseFloat(fiatAmount);
    if (!fiatAmount || amount <= 0 || !fiatCurrency || !cryptoCurrency) {
      setProviderQuotes([]);
      setSelectedProvider(null);
      setQuotesError(null);
      setIsStale(false);
      setLastFetchTime(null);
      return;
    }

    // Validate amount against currency-specific limits when buying
    if (!isSell) {
      const limits = getCurrencyLimits(fiatCurrency);
      if (amount < limits.min || amount > limits.max) {
        setProviderQuotes([]);
        setSelectedProvider(null);
        setQuotesError(null);
        return;
      }
    }

    setQuotesLoading(true);
    setQuotesError(null);
    setProviderErrors({});

    console.log('Fetching quotes with:', {
      transactionType,
      fiatCurrency,
      cryptoCurrency,
      network,
      paymentMethod,
      fiatAmount: isSell ? undefined : amount,
      cryptoAmount: isSell ? amount : undefined,
    });

    try {
      // Single API call — if config.provider is set, only query that one provider (ramp param)
      let response = await onrampService.compareQuotes({
        fiatCurrency,
        cryptoCurrency,
        isBuyOrSell: isSell ? 'SELL' : 'BUY',
        network,
        paymentMethod: paymentMethodManuallySelected.current ? paymentMethod : undefined,
        fiatAmount: isSell ? undefined : amount,
        cryptoAmount: isSell ? amount : undefined,
        ramp: config.provider || undefined,
      });

      // Check if aborted
      if (signal?.aborted) return;

      // If passing the specific paymentMethod yielded 0 valid quotes, retry without the strict paymentMethod
      // so we don't lock the user into an empty error state (e.g. Card selected for INR)
      if ((!response?.quotes || response.quotes.length === 0) && paymentMethodManuallySelected.current) {
        try {
          const fallbackResp = await onrampService.compareQuotes({
            fiatCurrency,
            cryptoCurrency,
            isBuyOrSell: isSell ? 'SELL' : 'BUY',
            network,
            fiatAmount: isSell ? undefined : amount,
            cryptoAmount: isSell ? amount : undefined,
            ramp: config.provider || undefined,
          });
          if (fallbackResp?.quotes && fallbackResp.quotes.length > 0) {
            response = fallbackResp;
            paymentMethodManuallySelected.current = false;
          }
        } catch (e) {
          // ignore fallback error and proceed with original response
        }
      }

      console.log('Compare quotes response:', response);

      // Extract quotes and metadata from response
      const { quotes = [], meta = {} } = response || {};
      const { providersFailed = [] } = meta;

      // Track failed providers
      if (providersFailed.length > 0) {
        const errors = {};
        providersFailed.forEach(provider => {
          errors[provider] = 'Quote unavailable';
        });
        setProviderErrors(errors);
      }

      // Filter by allowed providers if configured, then filter invalid quotes
      let validQuotes = quotes.filter(q => q && (isSell ? (q.payoutAmount > 0 || q.fiatAmount > 0) : q.cryptoAmount > 0));
      if (config.allowedProviders?.length > 0) {
        validQuotes = validQuotes.filter(q => config.allowedProviders.includes(q.provider));
      }

      // Sort by highest payout (best price first), then mark best
      validQuotes.sort((a, b) => {
        const payoutA = isSell ? (a.fiatAmount || a.payoutAmount || 0) : (a.cryptoAmount || 0);
        const payoutB = isSell ? (b.fiatAmount || b.payoutAmount || 0) : (b.cryptoAmount || 0);
        return payoutB - payoutA;
      });
      const bestPayout = isSell
        ? (validQuotes[0]?.fiatAmount || validQuotes[0]?.payoutAmount || 0)
        : (validQuotes[0]?.cryptoAmount || 0);
      const quotesWithBestPrice = validQuotes.map((quote) => {
        const currentPayout = isSell ? (quote.fiatAmount || quote.payoutAmount || 0) : (quote.cryptoAmount || 0);
        return {
          ...quote,
          isBestPrice: bestPayout > 0 && currentPayout === bestPayout,
        };
      });

      setProviderQuotes(quotesWithBestPrice);
      setLastFetchTime(Date.now());
      setIsStale(false);

      // Auto-select provider (prefer config param, then user manual choice, then current selection, then best price)
      if (quotesWithBestPrice.length > 0) {
        const configProvider = config.provider &&
          quotesWithBestPrice.find(q => q.provider === config.provider);
        const manualChoice = manualProviderSelectedRef.current &&
          quotesWithBestPrice.find(q => q.provider === manualProviderSelectedRef.current);
        const currentProvider = selectedProviderRef.current?.provider;
        const currentStillValid = currentProvider &&
          quotesWithBestPrice.find(q => q.provider === currentProvider);

        const newSelection = configProvider
          ? configProvider
          : manualChoice
            ? manualChoice
            : currentStillValid
              ? currentStillValid
              : quotesWithBestPrice[0];

        setSelectedProvider(newSelection);

        // Announce for screen readers
        if (isSell) {
          setAnnouncement(`Found ${quotesWithBestPrice.length} quotes. Best: ${quotesWithBestPrice[0]?.fiatAmount?.toFixed(2)} ${fiatCurrency}`);
        } else {
          setAnnouncement(`Found ${quotesWithBestPrice.length} quotes. Best: ${quotesWithBestPrice[0]?.cryptoAmount?.toFixed(6)} ${cryptoCurrency}`);
        }
      } else {
        setSelectedProvider(null);
        const group = tokenGroups.find(g => g.symbol === cryptoCurrency);
        const netObj = group?.networks.find(n => n.network === network);
        const netName = netObj?.networkName || (network ? network.charAt(0).toUpperCase() + network.slice(1) : '');

        const failureErrors = meta.failureErrors || [];
        const minError = failureErrors.find(e => e?.type === 'MinAmount' || /min/i.test(e?.message || ''));
        const maxError = failureErrors.find(e => e?.type === 'MaxAmount' || /max/i.test(e?.message || ''));

        if (minError) {
          setQuotesError(minError.message && !/transak|banxa|alchemy|guardarian|provider|endpoint/i.test(minError.message)
            ? minError.message
            : `The amount entered is below the minimum limit for ${isSell ? cryptoCurrency : fiatCurrency}. Please try a larger amount.`);
        } else if (maxError) {
          setQuotesError(maxError.message && !/transak|banxa|alchemy|guardarian|provider|endpoint/i.test(maxError.message)
            ? maxError.message
            : `The amount entered exceeds the maximum limit for ${isSell ? cryptoCurrency : fiatCurrency}. Please try a smaller amount.`);
        } else if (isSell) {
          setQuotesError(
            `No quotes available for selling ${cryptoCurrency}${netName ? ` on ${netName}` : ''}. Please try selecting a different network or cryptocurrency.`
          );
        } else {
          setQuotesError(
            `No quotes available for buying ${cryptoCurrency}${netName ? ` on ${netName}` : ''} with ${fiatCurrency}. Please try another payment method or amount.`
          );
        }
      }
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Error fetching quotes:', error);
      setQuotesError('Unable to retrieve quotes at this time. Please try again shortly.');
      setProviderQuotes([]);
      setSelectedProvider(null);
    } finally {
      if (!signal?.aborted) {
        setQuotesLoading(false);
      }
    }
  }, [transactionType, fiatAmount, fiatCurrency, cryptoCurrency, network, paymentMethod, config.allowedProviders, config.provider, getCurrencyLimits]);

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    fetchQuotes(abortControllerRef.current.signal);
  }, [fetchQuotes]);

  // Debounce quote fetching when inputs change with AbortController
  useEffect(() => {
    if (optionsLoading) return;

    const timer = setTimeout(() => {
      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      fetchQuotes(abortControllerRef.current.signal);
    }, 500);

    return () => {
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fiatAmount, fiatCurrency, cryptoCurrency, network, paymentMethod, optionsLoading, fetchQuotes, fetchTrigger]);

  // Handle wallet validation
  const validateWallet = (address, required = false) => {
    if (!address || address.trim() === '') {
      if (required) {
        setWalletError('Wallet address is required');
        return false;
      }
      setWalletError(null);
      return true;
    }

    const trimmed = address.trim();
    if (trimmed.startsWith('0x')) {
      if (trimmed.length !== 42) {
        setWalletError('Address should be 42 characters');
        return false;
      }
      if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
        setWalletError('Invalid address format');
        return false;
      }
    } else if (trimmed.length < 20) {
      setWalletError('Address seems too short');
      return false;
    }

    setWalletError(null);
    return true;
  };

  // Handle provider selection with bridge notification
  const handleProviderSelect = (quote) => {
    setManualProvider(quote.provider);
    manualProviderSelectedRef.current = quote.provider;
    setSelectedProvider(quote);
    setWalletError(null);

    // If current payment method is not supported by selected provider, switch to provider's first available method
    const supportedIds = (quote.availablePaymentMethods || []).map(m => m.paymentTypeId || m.id || m.code);
    if (supportedIds.length > 0 && (!paymentMethod || !supportedIds.includes(paymentMethod))) {
      setPaymentMethod(supportedIds[0]);
    }

    if (bridge) bridge.onQuoteSelected(quote);
  };

  // Dismiss awaiting payment overlay (return to form)
  const handleDismissAwaiting = () => {
    setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
    popupWindowRef.current = null;
  };

  // Dismiss success banner
  const handleDismissCompletion = () => {
    setPaymentComplete(null);
  };

  /**
   * Handle buy button click
   *
   * When isLocked=true (payment link mode), uses the custom onCheckout handler
   * which calls the payment session checkout API with locked values.
   * When isLocked=false (normal mode), calls the standard widget URL API.
   */
  const handleBuy = async () => {
    if (!selectedProvider) return;

    // Wallet address is always required
    if (!walletAddress || walletAddress.trim() === '') {
      setWalletError('Wallet address is required');
      return;
    }

    if (!validateWallet(walletAddress, true)) {
      return;
    }

    setLoading(true);
    try {
      // Notify parent if embedded
      if (bridge) bridge.onProviderOpened(selectedProvider.provider);

      // If locked mode with custom checkout handler (payment link flow)
      // Use the custom handler which validates against the payment session
      if (isLocked && config.onCheckout) {
        await config.onCheckout({
          provider: selectedProvider.provider,
          paymentMethod: paymentMethod || undefined,
          walletAddress: walletAddress || undefined,
        });
        return;
      }

      const isSell = transactionType === 'SELL';
      const purchasePayload = {
        walletAddress,
        provider: selectedProvider.provider,
        fiatAmount: isSell ? (selectedProvider.fiatAmount || undefined) : parseFloat(fiatAmount),
        cryptoAmount: isSell ? parseFloat(fiatAmount) : (selectedProvider.cryptoAmount || undefined),
        fiatCurrency,
        cryptoCurrency,
        network,
        paymentMethod: paymentMethod || undefined,
        isBuyOrSell: transactionType,
      };

      // Wallet mode — create WalletPurchase record first, then open provider widget
      if (isWalletMode) {
        const result = await walletPurchaseService.initiatePurchase(purchasePayload);

        if (result?.purchaseId) {
          setActivePurchaseId(result.purchaseId);
          if (bridge) bridge.onPurchaseInitiated({
            purchaseId: result.purchaseId,
            provider: selectedProvider.provider,
            walletAddress,
            fiatAmount: purchasePayload.fiatAmount,
            cryptoAmount: purchasePayload.cryptoAmount,
            fiatCurrency,
            cryptoCurrency,
            network,
            paymentMethod: purchasePayload.paymentMethod,
            widgetUrl: result.widgetUrl,
            isBuyOrSell: transactionType,
            type: isSell ? 'OFF' : 'ON',
          });
        }

        if (result?.widgetUrl) {
          const providerDisplayName =
            selectedProvider.provider.charAt(0).toUpperCase() +
            selectedProvider.provider.slice(1);
          const embedAs = result.embedAs || 'tab';

          if (embedAs === 'iframe') {
            // In-page iframe — no popup, no navigation. The awaiting-payment
            // overlay renders the iframe inline (see render block below).
            setAwaitingPayment({
              active: true,
              providerName: providerDisplayName,
              widgetUrl: result.widgetUrl,
              popupBlocked: false,
              embedAs: 'iframe',
            });
          } else {
            // Open tab only after we have the URL — no blank tab flash
            const newTab = window.open(result.widgetUrl, '_blank', 'noopener,noreferrer');
            if (!newTab) {
              // Popup blocked — redirect in same window (navigates away, no polling needed)
              window.location.href = result.widgetUrl;
            } else {
              // Popup opened — track it and activate polling + BroadcastChannel listeners
              // so TRANSACTION_COMPLETE fires on success (same pattern as normal mode).
              // The overlay UI is hidden in wallet mode (guarded by !isWalletMode at render),
              // so setting awaitingPayment.active only activates the event listeners.
              popupWindowRef.current = newTab;
              setAwaitingPayment({
                active: true,
                providerName: providerDisplayName,
                widgetUrl: result.widgetUrl,
                popupBlocked: false,
                embedAs: 'tab',
              });
            }
          }
        } else {
          throw new Error('Failed to get widget URL');
        }

        if (bridge) bridge.onProviderOpened(selectedProvider.provider);
        return;
      }

      // Normal mode — create WalletPurchase record, get URL, then open tab
      const purchaseResult = await walletPurchaseService.initiatePurchase(purchasePayload);

      if (purchaseResult?.purchaseId) {
        setActivePurchaseId(purchaseResult.purchaseId);
        if (bridge) bridge.onPurchaseInitiated({
          purchaseId: purchaseResult.purchaseId,
          provider: selectedProvider.provider,
          walletAddress,
          fiatAmount: purchasePayload.fiatAmount,
          cryptoAmount: purchasePayload.cryptoAmount,
          fiatCurrency,
          cryptoCurrency,
          network,
          paymentMethod: purchasePayload.paymentMethod,
          widgetUrl: purchaseResult.widgetUrl,
          isBuyOrSell: transactionType,
          type: isSell ? 'OFF' : 'ON',
        });
      }

      if (purchaseResult?.widgetUrl) {
        const providerDisplayName = selectedProvider.provider.charAt(0).toUpperCase() + selectedProvider.provider.slice(1);
        const embedAs = purchaseResult.embedAs || 'tab';

        if (embedAs === 'iframe') {
          // In-page iframe — overlay renders <iframe> inline (no popup).
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: purchaseResult.widgetUrl,
            popupBlocked: false,
            embedAs: 'iframe',
          });
        } else {
          // Open tab only after we have the URL — no blank tab flash
          const newTab = window.open(purchaseResult.widgetUrl, '_blank', 'noopener,noreferrer');
          const popupBlocked = !newTab || newTab.closed;

          if (newTab) {
            popupWindowRef.current = newTab;
          }

          // Show awaiting payment overlay
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: purchaseResult.widgetUrl,
            popupBlocked,
            embedAs: 'tab',
          });
        }
      } else {
        throw new Error('Failed to get widget URL');
      }
    } catch (error) {
      console.error('Error opening widget:', error);
      if (bridge) bridge.onError({ type: 'WIDGET_OPEN_ERROR', message: error.message });
      setWalletError(error.message || 'Failed to open widget. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Listen for widget events
  useEffect(() => {
    const handleWidgetEvent = (event) => {
      if (event.data?.event_id === 'TRANSAK_ORDER_CREATED' && event.data.data?.order_id) {
        // Notify parent if embedded
        if (bridge) bridge.onTransactionComplete(event.data.data);
        // Redirect to successUrl if configured (for exchange/wallet iframe integration)
        if (config.successUrl) {
          const url = new URL(config.successUrl);
          url.searchParams.set('orderId', event.data.data.order_id);
          if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
          window.location.href = url.toString();
        }
      }
    };
    window.addEventListener('message', handleWidgetEvent);
    return () => window.removeEventListener('message', handleWidgetEvent);
  }, [bridge, config.successUrl, config.orderId]);

  // Poll purchase status when activePurchaseId is set AND payment overlay is showing
  useEffect(() => {
    if (!activePurchaseId || !awaitingPayment.active) return;

    const TERMINAL_STATUSES = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED', 'REFUNDED'];
    const POLL_INTERVAL = 10000; // 10 seconds
    const MAX_ERRORS = 3; // Stop after 3 consecutive errors
    let errorCount = 0;
    let intervalId = null;

    const poll = async () => {
      try {
        const data = await walletPurchaseService.getPurchaseStatus(activePurchaseId);
        errorCount = 0; // Reset on success
        setPurchaseStatus(data);
        if (TERMINAL_STATUSES.includes(data?.status)) {
          if (intervalId) clearInterval(intervalId);

          // Dismiss overlay and stop polling completely
          setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
          setActivePurchaseId(null);

          if (data.status === 'COMPLETED') {
            if (bridge) bridge.onTransactionComplete({ purchaseId: activePurchaseId, txHash: data.txHash });
            if (config.successUrl) {
              const url = new URL(config.successUrl);
              url.searchParams.set('purchaseId', activePurchaseId);
              if (data.txHash) url.searchParams.set('txHash', data.txHash);
              window.location.href = url.toString();
            }
          } else if (data.status === 'FAILED' && config.failureUrl) {
            window.location.href = config.failureUrl;
          }
        }
      } catch (err) {
        errorCount++;
        console.error(`Error polling purchase status (${errorCount}/${MAX_ERRORS}):`, err);
        if (errorCount >= MAX_ERRORS && intervalId) {
          clearInterval(intervalId);
          // Dismiss overlay and stop polling completely
          setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
          setActivePurchaseId(null);
          console.warn('Stopped polling: too many consecutive errors');
        }
      }
    };

    // Delay first poll by 5s to let the provider widget load
    const initialTimeout = setTimeout(() => {
      poll();
      intervalId = setInterval(poll, POLL_INTERVAL);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [activePurchaseId, awaitingPayment.active, bridge, config.successUrl, config.failureUrl]);

  // Normal mode: Listen for payment completion from success page (BroadcastChannel + localStorage)
  useEffect(() => {
    if (!awaitingPayment.active) return;

    const handlePaymentComplete = (data) => {
      const upper = String(data.status || '').toUpperCase();
      const isTerminal = ['COMPLETED', 'CANCELLED', 'FAILED'].includes(upper);
      if (!isTerminal) return;

      // Cross-tab safety: the kuberpay-payment channel is shared by every
      // provider redirect on this origin. Both the widget flow (purchase.id)
      // and the payment-gateway flow (sessionId) end up broadcasting through
      // it. We only act if the broadcasted orderId matches THIS tab's active
      // wallet purchase — otherwise a payment completed in another tab/flow
      // would prematurely close our awaiting overlay.
      //
      // Skipped only when both sides are present (legacy success pages that
      // forget to pass orderId still flow through, since we have no way to
      // distinguish them from a stale broadcast).
      if (activePurchaseId && data.orderId && data.orderId !== activePurchaseId) {
        console.log('BuyCrypto: ignoring broadcast for different orderId', {
          received: data.orderId,
          expected: activePurchaseId,
        });
        return;
      }

      console.log(`BuyCrypto: Payment ${upper} detected via BroadcastChannel/localStorage:`, data);

      if (upper === 'COMPLETED') {
        setPaymentComplete({
          status: 'COMPLETED',
          orderId: data.orderId,
          txHash: data.txHash,
          timestamp: data.timestamp,
        });
      }

      // Clear awaiting overlay + stop polling for every terminal state, so the
      // user is never stuck staring at "waiting for payment" after they cancel
      // or fail at the provider.
      setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
      setActivePurchaseId(null);
      popupWindowRef.current = null;

      // Notify embedding parent (exchange/wallet iframe) via bridge.
      if (bridge) {
        if (upper === 'COMPLETED') {
          bridge.onTransactionComplete({
            orderId: data.orderId,
            txHash: data.txHash,
            status: 'COMPLETED',
          });
        } else if (bridge.onTransactionFailed) {
          bridge.onTransactionFailed({
            orderId: data.orderId,
            status: upper, // CANCELLED | FAILED
          });
        }
      }

      // Configured successUrl redirect ONLY on success — we don't want to
      // send the user away from the form on cancel/fail; they may want to retry.
      if (upper === 'COMPLETED' && config.successUrl) {
        setTimeout(() => {
          const url = new URL(config.successUrl);
          if (data.orderId) url.searchParams.set('orderId', data.orderId);
          if (data.txHash) url.searchParams.set('txHash', data.txHash);
          if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
          window.location.href = url.toString();
        }, 2000);
      }
    };

    // Method 1: BroadcastChannel (modern browsers)
    let channel = null;
    try {
      channel = new BroadcastChannel('kuberpay-payment');
      channel.onmessage = (event) => {
        if (event.data?.type === 'PAYMENT_STATUS') {
          handlePaymentComplete(event.data);
        }
      };
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }

    // Method 2: localStorage (fallback for Safari)
    const handleStorageChange = (e) => {
      if (e.key === 'kuberpay-payment-status' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          handlePaymentComplete(data);
        } catch (err) {
          console.warn('Failed to parse localStorage event:', err);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Check localStorage on mount (in case success page already set it)
    try {
      const storedStatus = localStorage.getItem('kuberpay-payment-status');
      if (storedStatus) {
        const data = JSON.parse(storedStatus);
        if (data.timestamp && Date.now() - data.timestamp < 30000) {
          handlePaymentComplete(data);
        }
        localStorage.removeItem('kuberpay-payment-status');
      }
    } catch (err) {
      console.warn('Failed to check localStorage:', err);
    }

    return () => {
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [awaitingPayment.active, bridge, config.successUrl, config.orderId, activePurchaseId]);

  // Normal mode: Monitor popup window — dismiss overlay if popup closed without completion
  useEffect(() => {
    if (!awaitingPayment.active || !popupWindowRef.current) return;

    const checkPopup = setInterval(() => {
      try {
        if (popupWindowRef.current && popupWindowRef.current.closed) {
          clearInterval(checkPopup);
          // Only dismiss if payment hasn't completed yet
          if (!paymentComplete) {
            setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
            setActivePurchaseId(null);
            popupWindowRef.current = null;
          }
        }
      } catch (e) {
        // Cross-origin errors are expected — ignore
      }
    }, 1000);

    return () => clearInterval(checkPopup);
  }, [awaitingPayment.active, paymentComplete]);

  // Get code helper
  const getCode = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.code || item.symbol || item.id || item.name || '';
  };

  const getName = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.displayName || item.name || item.code || '';
  };

  // Get best quote for display
  const isSell = transactionType === 'SELL';
  const bestQuote = providerQuotes.find(q => q.isBestPrice) || providerQuotes[0];
  const displayCryptoAmount = useMemo(() => {
    if (!fiatAmount || parseFloat(fiatAmount) <= 0) return '0.00';
    const quote = selectedProvider || bestQuote;
    if (!quote) return '0.00';

    if (isSell) {
      const amt = quote.fiatAmount || quote.payoutAmount;
      if (!amt || isNaN(amt) || Number(amt) <= 0) return '0.00';
      return Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      const amt = quote.cryptoAmount || quote.payoutAmount;
      if (!amt || isNaN(amt) || Number(amt) <= 0) return '0.00';
      const num = Number(amt);
      const isStable = ['USDT', 'USDC', 'DAI', 'BUSD', 'FDUSD', 'TUSD', 'USDD'].includes((cryptoCurrency || '').toUpperCase());
      if (isStable) {
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      }
      if (num >= 100) {
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      }
      if (num >= 1) {
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
      }
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
    }
  }, [fiatAmount, selectedProvider, bestQuote, isSell, cryptoCurrency]);

  // Exchange rate from selected provider (calculate fiat per crypto)
  const fiatPerCrypto = selectedProvider?.cryptoAmount > 0
    ? selectedProvider.fiatAmount / selectedProvider.cryptoAmount
    : 0;
  const exchangeRate = fiatPerCrypto > 0
    ? `1 ${cryptoCurrency} ≈ ${fiatPerCrypto.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${fiatCurrency}`
    : '';

  // Get filtered payment methods (provider-aware: prioritize methods supported by returned quotes)
  const filteredPaymentMethods = useMemo(() => {
    // 0. If user has explicitly chosen a provider via the modal, show ONLY methods supported by that provider
    if (manualProvider && selectedProvider?.availablePaymentMethods?.length > 0) {
      const list = selectedProvider.availablePaymentMethods.map((pm, idx) => ({
        id: pm.paymentTypeId || pm.id || pm.code,
        code: pm.paymentTypeId || pm.id || pm.code,
        name: pm.name,
        icon: pm.icon || null,
        recommended: idx === 0,
      }));
      return list;
    }

    // 1. Gather all methods supported by any valid quote returned for this pair
    const quoteMethodsMap = new Map();
    if (providerQuotes && providerQuotes.length > 0) {
      providerQuotes.forEach(q => {
        (q.availablePaymentMethods || []).forEach(pm => {
          const id = pm.paymentTypeId || pm.id || pm.code;
          if (id && !quoteMethodsMap.has(id)) {
            quoteMethodsMap.set(id, {
              id,
              code: id,
              name: pm.name,
              icon: pm.icon || null,
              providers: [q.provider],
            });
          } else if (id) {
            quoteMethodsMap.get(id).providers.push(q.provider);
          }
        });
      });
    }

    if (quoteMethodsMap.size > 0) {
      const list = Array.from(quoteMethodsMap.values());
      if (isSell) {
        list.sort((a, b) => {
          const aId = (a.id || a.code || a.name || '').toLowerCase();
          const bId = (b.id || b.code || b.name || '').toLowerCase();
          const aIsBank = aId.includes('bank') || aId.includes('ach') || aId.includes('sepa') || aId.includes('wire');
          const bIsBank = bId.includes('bank') || bId.includes('ach') || bId.includes('sepa') || bId.includes('wire');
          if (aIsBank && !bIsBank) return -1;
          if (!aIsBank && bIsBank) return 1;
          return 0;
        });
      }
      return list.map((m, idx) => ({
        ...m,
        recommended: idx === 0,
      }));
    }

    // 2. Fallback: methods for selected provider if available
    const providerMethods = selectedProvider?.availablePaymentMethods;
    if (providerMethods && providerMethods.length > 0) {
      const list = providerMethods.map(pm => ({
        id: pm.paymentTypeId,
        code: pm.paymentTypeId,
        name: pm.name,
        icon: pm.icon || null,
      }));
      if (isSell) {
        list.sort((a, b) => {
          const aId = (a.id || a.code || a.name || '').toLowerCase();
          const bId = (b.id || b.code || b.name || '').toLowerCase();
          const aIsBank = aId.includes('bank') || aId.includes('ach') || aId.includes('sepa') || aId.includes('wire');
          const bIsBank = bId.includes('bank') || bId.includes('ach') || bId.includes('sepa') || bId.includes('wire');
          if (aIsBank && !bIsBank) return -1;
          if (!aIsBank && bIsBank) return 1;
          return 0;
        });
      }
      return list.map((m, idx) => ({
        ...m,
        recommended: idx === 0,
      }));
    }

    if (isSell) {
      return [
        { id: 'bank_transfer', code: 'bank_transfer', name: 'Bank Transfer (ACH / Wire / SEPA)', recommended: true },
        { id: 'ach_bank_transfer', code: 'ach_bank_transfer', name: 'ACH Bank Transfer' },
        { id: 'sepa_bank_transfer', code: 'sepa_bank_transfer', name: 'SEPA Bank Transfer' },
      ];
    }

    // 3. Fallback: global payment methods filtered by currency
    return paymentMethods.filter(method => {
      if (method.currencies && method.currencies.length > 0) {
        return method.currencies.includes(fiatCurrency);
      }
      return true;
    });
  }, [manualProvider, providerQuotes, selectedProvider, paymentMethods, fiatCurrency, isSell]);

  // Auto-select valid payment method when provider changes
  useEffect(() => {
    if (!selectedProvider?.availablePaymentMethods?.length) return;

    const providerMethodIds = selectedProvider.availablePaymentMethods.map(
      m => m.paymentTypeId
    );

    if (isSell) {
      // In sell mode, prefer bank transfer / ACH / wire payout methods if not currently set to a valid method
      const bankMethod = selectedProvider.availablePaymentMethods.find(m => {
        const id = (m.paymentTypeId || m.name || '').toLowerCase();
        return id.includes('bank') || id.includes('ach') || id.includes('sepa') || id.includes('wire');
      });
      if (bankMethod && (!paymentMethod || !providerMethodIds.includes(paymentMethod))) {
        setPaymentMethod(bankMethod.paymentTypeId);
        return;
      }
    }

    if (paymentMethod && !providerMethodIds.includes(paymentMethod)) {
      setPaymentMethod(providerMethodIds[0]);
    } else if (!paymentMethod && providerMethodIds.length > 0) {
      setPaymentMethod(providerMethodIds[0]);
    }
  }, [selectedProvider, isSell]);

  const selectedPaymentMethod = filteredPaymentMethods.find(
    m => (m.id || m.code) === paymentMethod
  ) || filteredPaymentMethods[0];

  // Keyboard handler for provider cards
  const handleProviderKeyDown = (e, quote) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleProviderSelect(quote);
    }
  };

  // Multi-theme class support (dark, cyber, mono, light)
  const themeClass = useMemo(() => {
    const t = (config.theme || '').toLowerCase().trim();
    if (t === 'cyber' || t === 'cyberpunk') return 'cyber-theme';
    if (t === 'mono' || t === 'monochrome') return 'mono-theme';
    if (t === 'light') return 'light-theme';
    return 'dark-theme';
  }, [config.theme]);

  // Loading state
  if (optionsLoading) {
    return (
      <div className={`widget-wrapper ${isEmbedded ? 'embedded' : ''} ${themeClass}`}>
        <div className={`widget-container ${isEmbedded ? 'embedded' : ''}`}>
          <div className="widget-body" style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div className="loading-skeleton" style={{ width: 60, height: 60, borderRadius: '50%', margin: '0 auto 16px' }}></div>
            <div className="loading-skeleton" style={{ width: 150, height: 20, margin: '0 auto' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Render fiat currency dropdown
  const renderFiatSelector = () => (
    <div className="dropdown-container" ref={fiatDropdownRef}>
      <button
        className={`currency-selector ${isFiatLocked ? 'locked' : ''}`}
        onClick={() => {
          if (isFiatLocked) return;
          setShowFiatDropdown(!showFiatDropdown);
        }}
        disabled={isFiatLocked}
        aria-label={`Select fiat currency, currently ${fiatCurrency}`}
        aria-expanded={showFiatDropdown}
        aria-haspopup="listbox"
      >
        <span className="currency-flag" aria-hidden="true">
          {CURRENCY_FLAGS[fiatCurrency] || '💵'}
        </span>
        <span className="currency-code">{fiatCurrency}</span>
        {!isFiatLocked && <span className="currency-dropdown-icon" aria-hidden="true">▼</span>}
      </button>

      {showFiatDropdown && !isFiatLocked && (
        <div className="currency-dropdown-menu" role="listbox" aria-label="Fiat currencies">
          {fiatCurrencies.map((currency) => {
            const code = getCode(currency);
            return (
              <button
                key={code}
                className={`currency-option ${code === fiatCurrency ? 'selected' : ''}`}
                onClick={() => {
                  setFiatCurrency(code);
                  setShowFiatDropdown(false);
                }}
                role="option"
                aria-selected={code === fiatCurrency}
              >
                <span className="currency-flag" aria-hidden="true">{CURRENCY_FLAGS[code] || '💵'}</span>
                <span className="currency-code">{code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render cryptocurrency & network selector
  const renderCryptoSelector = () => (
    <div className="dropdown-container" ref={cryptoDropdownRef}>
      <button
        className={`currency-selector crypto-selector-expanded ${isCryptoLocked ? 'locked' : ''}`}
        onClick={() => {
          if (isCryptoLocked) return;
          setShowCryptoDropdown(!showCryptoDropdown);
          setCryptoSearch('');
        }}
        disabled={isCryptoLocked}
        aria-label={`Select cryptocurrency, currently ${cryptoCurrency} on ${network}`}
        aria-expanded={showCryptoDropdown}
        aria-haspopup="listbox"
      >
        {getCryptoIcon(cryptoCurrency, network) ? (
          <img
            className="crypto-icon"
            src={getCryptoIcon(cryptoCurrency, network)}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
            }}
          />
        ) : null}
        <span
          className="crypto-icon"
          style={{
            background: CRYPTO_COLORS[cryptoCurrency] || '#627eea',
            display: getCryptoIcon(cryptoCurrency, network) ? 'none' : 'flex',
          }}
          aria-hidden="true"
        >
          {cryptoCurrency.charAt(0)}
        </span>
        <div className="crypto-selector-info">
          <span className="currency-code">{cryptoCurrency}</span>
          <span
            className="network-badge-small"
            style={{ background: NETWORK_COLORS[network] || '#627eea' }}
          >
            {network.charAt(0).toUpperCase() + network.slice(1)}
          </span>
        </div>
        {!isCryptoLocked && <span className="currency-dropdown-icon" aria-hidden="true">▼</span>}
      </button>

      {showCryptoDropdown && !isCryptoLocked && (
        <div className="crypto-dropdown-menu" role="listbox" aria-label="Cryptocurrencies">

          {/* ── STEP 1: Select Token ── */}
          {cryptoDropdownStep === 'tokens' && (
            <>
              <div className="crypto-dropdown-header">
                <button
                  className="crypto-dropdown-back"
                  onClick={() => { setShowCryptoDropdown(false); setCryptoSearch(''); }}
                  aria-label="Close"
                >
                  ←
                </button>
                <span className="crypto-dropdown-title">{t('selectToken')}</span>
              </div>
              <div className="crypto-search-container">
                <input
                  type="text"
                  className="crypto-search-input"
                  placeholder={t('searchToken')}
                  value={cryptoSearch}
                  onChange={(e) => setCryptoSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="crypto-options-list">
                {tokenGroups.length === 0 && (
                  <div className="crypto-empty-state" style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                    {t('noTokensAvailable', { fiat: fiatCurrency })}
                  </div>
                )}
                {tokenGroups
                  .filter((group) => {
                    if (!cryptoSearch) return true;
                    const s = cryptoSearch.toLowerCase();
                    return (
                      group.symbol.toLowerCase().includes(s) ||
                      group.name.toLowerCase().includes(s) ||
                      group.networks.some(n => (n.networkName || '').toLowerCase().includes(s))
                    );
                  })
                  .map((group) => {
                    const isSelected = group.symbol === cryptoCurrency;
                    const hasMultipleNetworks = group.networks.length > 1;
                    return (
                      <button
                        key={group.symbol}
                        className={`crypto-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (hasMultipleNetworks) {
                            // Multi-network token → go to step 2
                            setPendingTokenGroup(group);
                            setCryptoDropdownStep('network');
                            setCryptoSearch('');
                          } else {
                            // Single network → select immediately
                            const net = group.networks[0];
                            setCryptoCurrency(group.symbol);
                            if (net?.network) setNetwork(net.network);
                            setShowCryptoDropdown(false);
                            setCryptoDropdownStep('tokens');
                            setCryptoSearch('');
                          }
                        }}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <div className="crypto-option-left">
                          {group.icon ? (
                            <img
                              className="crypto-icon-large"
                              src={group.icon}
                              alt=""
                              aria-hidden="true"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                              }}
                            />
                          ) : null}
                          <span
                            className="crypto-icon-large"
                            style={{
                              background: CRYPTO_COLORS[group.symbol] || '#627eea',
                              display: group.icon ? 'none' : 'flex',
                            }}
                            aria-hidden="true"
                          >
                            {group.symbol.charAt(0)}
                          </span>
                          <div className="crypto-option-info">
                            <span className="crypto-option-name">{group.name}</span>
                            <span className="crypto-option-code">{group.symbol}</span>
                          </div>
                        </div>
                        {hasMultipleNetworks ? (
                          <span className="network-count-badge">
                            {group.networks.length} networks <span aria-hidden="true">›</span>
                          </span>
                        ) : (
                          <span
                            className="network-badge-single"
                            style={{ background: NETWORK_COLORS[group.networks[0]?.network] || '#627eea' }}
                          >
                            {group.networks[0]?.networkName || group.networks[0]?.network}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </>
          )}

          {/* ── STEP 2: Select Network for chosen token ── */}
          {cryptoDropdownStep === 'network' && pendingTokenGroup && (
            <>
              <div className="crypto-dropdown-header">
                <button
                  className="crypto-dropdown-back"
                  onClick={() => { setCryptoDropdownStep('tokens'); setPendingTokenGroup(null); }}
                  aria-label="Back to tokens"
                >
                  ←
                </button>
                <span className="crypto-dropdown-title">
                  {t('selectNetwork')}
                </span>
              </div>
              <div className="network-step-token-info">
                {pendingTokenGroup.icon ? (
                  <img className="network-step-token-icon" src={pendingTokenGroup.icon} alt="" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                ) : null}
                <span
                  className="network-step-token-icon"
                  style={{ background: CRYPTO_COLORS[pendingTokenGroup.symbol] || '#627eea', display: pendingTokenGroup.icon ? 'none' : 'flex' }}
                >
                  {pendingTokenGroup.symbol.charAt(0)}
                </span>
                <div>
                  <div className="network-step-token-name">{pendingTokenGroup.name}</div>
                  <div className="network-step-token-subtitle">{t('tokenAvailableNetworks', { symbol: pendingTokenGroup.symbol, count: pendingTokenGroup.networks.length })}</div>
                </div>
              </div>
              <div className="network-step-list">
                {pendingTokenGroup.networks
                  .sort((a, b) => {
                    const POPULAR_NETS = ['ethereum', 'polygon', 'bsc', 'tron', 'solana', 'arbitrum', 'base', 'optimism'];
                    const ai = POPULAR_NETS.indexOf((a.network || '').toLowerCase());
                    const bi = POPULAR_NETS.indexOf((b.network || '').toLowerCase());
                    if (ai !== -1 && bi !== -1) return ai - bi;
                    if (ai !== -1) return -1;
                    if (bi !== -1) return 1;
                    return (a.networkName || '').localeCompare(b.networkName || '');
                  })
                  .map((net) => {
                    const isCurrentNetwork = net.network === network && pendingTokenGroup.symbol === cryptoCurrency;
                    return (
                      <button
                        key={net.network}
                        className={`network-step-item ${isCurrentNetwork ? 'selected' : ''}`}
                        onClick={() => {
                          setCryptoCurrency(pendingTokenGroup.symbol);
                          setNetwork(net.network);
                          setShowCryptoDropdown(false);
                          setCryptoDropdownStep('tokens');
                          setPendingTokenGroup(null);
                          setCryptoSearch('');
                        }}
                      >
                        <span
                          className="network-step-dot"
                          style={{ background: NETWORK_COLORS[net.network] || '#627eea' }}
                        />
                        <div className="network-step-info">
                          <span className="network-step-name">{net.networkName || net.network}</span>
                        </div>
                        {isCurrentNetwork && <span className="network-step-check">✓</span>}
                      </button>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Screen reader live region */}
      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {announcement}
      </div>

      <div className={`widget-wrapper ${isEmbedded ? 'embedded' : ''} ${themeClass}`}>
        <div className={`widget-container ${isEmbedded ? 'embedded' : ''}`}>
          {/* Header */}
          {!config.hideHeader && (
            <div className="widget-header">
              <div className="widget-logo">
                <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
                  <circle cx="12" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none" />
                  <circle cx="28" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none" />
                </svg>
              </div>

              <div className="widget-title">
                {transactionType === 'SELL' ? t('sellCrypto') : t('buyCrypto')}
              </div>

              <button
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={quotesLoading}
                aria-label={t('refreshQuotes')}
                title={t('refreshQuotes')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
              </button>
            </div>
          )}

          {/* Buy/Sell Tab Navigator */}
          {!config.lockType && (
            <div className="ramp-tabs-nav">
              <button
                type="button"
                className={`ramp-tab-btn ${transactionType === 'BUY' ? 'active' : ''}`}
                onClick={() => {
                  if (transactionType !== 'BUY') {
                    setTransactionType('BUY');
                    setFiatAmount('');
                    setProviderQuotes([]);
                    setSelectedProvider(null);
                    setManualProvider(null);
                    manualProviderSelectedRef.current = null;
                  }
                }}
              >
                {t('buy')}
              </button>
              <button
                type="button"
                className={`ramp-tab-btn ${transactionType === 'SELL' ? 'active' : ''}`}
                onClick={() => {
                  if (transactionType !== 'SELL') {
                    setTransactionType('SELL');
                    setFiatAmount('');
                    setProviderQuotes([]);
                    setSelectedProvider(null);
                    setManualProvider(null);
                    manualProviderSelectedRef.current = null;
                    if (network === 'arbitrum' && cryptoCurrency === 'USDC') {
                      setNetwork('ethereum');
                    }
                  }
                }}
              >
                {t('sell')}
              </button>
            </div>
          )}

          {/* Body */}
          <div className="widget-body">
            {/* Staleness warning */}
            {isStale && !quotesLoading && providerQuotes.length > 0 && parseFloat(fiatAmount) > 0 && (
              <div className="stale-warning" role="alert">
                <span>{t('quotesOutdated')}</span>
                <button onClick={handleRefresh} className="refresh-link">{t('refresh')}</button>
              </div>
            )}

            {/* Input Section (You spend / You sell) */}
            <div className="input-section">
              <label htmlFor="fiat-amount" className="input-label">
                {isSell ? t('youSell') : t('youSpend')}
                {isAmountLocked && <span className="locked-badge">Locked</span>}
              </label>
              <div className="input-row">
                <input
                  id="fiat-amount"
                  type="number"
                  className={`amount-input ${amountError ? 'error' : ''} ${isAmountLocked ? 'locked' : ''}`}
                  value={fiatAmount}
                  onChange={(e) => {
                    if (isAmountLocked) return;
                    let val = e.target.value;
                    if (val.length > 15) {
                      val = val.slice(0, 15);
                    }
                    setFiatAmount(val);
                    validateAmount(val);
                    setFetchTrigger(prev => prev + 1);
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  maxLength={15}
                  disabled={isAmountLocked}
                  readOnly={isAmountLocked}
                  aria-describedby={amountError ? 'amount-error' : undefined}
                  aria-invalid={!!amountError}
                />

                {isSell ? renderCryptoSelector() : renderFiatSelector()}
              </div>
              {amountError && (
                <div id="amount-error" className="amount-error" role="alert">{amountError}</div>
              )}
            </div>

            {/* Output Section (You get / You receive) */}
            <div className="input-section">
              <span className="input-label" id="crypto-output-label">
                {isSell ? t('youReceiveEst') : t('youReceive')}
                {(isSell ? isFiatLocked : isCryptoLocked) && <span className="locked-badge">Locked</span>}
              </span>
              <div className="input-row">
                <span
                  className="amount-display"
                  aria-labelledby="crypto-output-label"
                  aria-live="polite"
                >
                  {quotesLoading ? (
                    <span className="loading-dots" aria-label="Loading">...</span>
                  ) : displayCryptoAmount}
                </span>

                {isSell ? renderFiatSelector() : renderCryptoSelector()}
              </div>
            </div>

            {/* Network indicator — clickable to change network */}
            {!isCryptoLocked && (
              <div className="network-change-row">
                <span className="network-change-label">{t('network')}</span>
                <button
                  className="network-change-btn"
                  onClick={() => {
                    // Find current token's group and go directly to network step
                    const group = tokenGroups.find(g => g.symbol === cryptoCurrency);
                    if (group && group.networks.length > 1) {
                      setPendingTokenGroup(group);
                      setCryptoDropdownStep('network');
                      setShowCryptoDropdown(true);
                    } else {
                      // Single network token — open token selector instead
                      setCryptoDropdownStep('tokens');
                      setShowCryptoDropdown(true);
                    }
                  }}
                >
                  <span
                    className="network-change-dot"
                    style={{ background: NETWORK_COLORS[network] || '#627eea' }}
                  />
                  <span className="network-change-name">
                    {(() => {
                      const group = tokenGroups.find(g => g.symbol === cryptoCurrency);
                      const net = group?.networks.find(n => n.network === network);
                      return net?.networkName || network.charAt(0).toUpperCase() + network.slice(1);
                    })()}
                  </span>
                  <span className="network-change-arrow" aria-hidden="true">›</span>
                </button>
              </div>
            )}

            {/* Exchange Rate */}
            {exchangeRate && (
              <div className="exchange-rate-display" aria-label={`Exchange rate: ${exchangeRate}`}>
                {exchangeRate}
              </div>
            )}

            {/* Best Price Section - Single provider display with "See All" */}
            {(quotesLoading || selectedProvider || (quotesError && parseFloat(fiatAmount) > 0)) && (
              <div className="best-price-section" role="region" aria-label="Best price">
                {quotesLoading ? (
                  <div className="best-price-loading" aria-busy="true">
                    <div className="provider-skeleton"></div>
                  </div>
                ) : (quotesError && parseFloat(fiatAmount) > 0) ? (
                  <div className="providers-error" role="alert">{quotesError}</div>
                ) : selectedProvider ? (
                  <div className="best-price-card">
                    <div className="best-price-header">
                      <span className="best-price-label">{t('bestRate')}</span>
                      <div className="best-price-header-right">
                        <div className="best-price-provider">
                          <div className="provider-card-logo small">
                            {(providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]) ? (
                              <img
                                src={providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                }}
                              />
                            ) : null}
                            <span style={{ display: (providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]) ? 'none' : 'flex' }}>
                              {selectedProvider.provider.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="best-price-provider-name">
                            via {selectedProvider.provider.charAt(0).toUpperCase() + selectedProvider.provider.slice(1)}
                          </span>
                        </div>
                        {providerQuotes.length > 1 && (
                          <button
                            className="see-all-btn"
                            onClick={() => setShowProvidersModal(true)}
                            aria-label={`See all ${providerQuotes.length} provider quotes`}
                          >
                            See All ({providerQuotes.length})
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="best-price-details">
                      <div className="best-price-fee">
                        <button
                          className="fee-toggle"
                          onClick={() => setShowFeeBreakdown(showFeeBreakdown === selectedProvider.provider ? null : selectedProvider.provider)}
                          aria-expanded={showFeeBreakdown === selectedProvider.provider}
                          aria-label="Show fee breakdown"
                        >
                          {selectedProvider.fees?.total > 0 ? `Fee: ${selectedProvider.fees.total.toFixed(2)} ${fiatCurrency}` : 'View fees'}
                          <span aria-hidden="true">{showFeeBreakdown === selectedProvider.provider ? '▲' : '▼'}</span>
                        </button>
                      </div>
                    </div>
                    {showFeeBreakdown === selectedProvider.provider && (
                      <div className="fee-breakdown" aria-label="Fee breakdown">
                        <div className="fee-row">
                          <span>Network fee</span>
                          <span>{selectedProvider.fees?.network?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                        <div className="fee-row">
                          <span>Provider fee</span>
                          <span>{(selectedProvider.fees?.service ?? selectedProvider.fees?.provider)?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                        <div className="fee-row total">
                          <span>Total fees</span>
                          <span>{selectedProvider.fees?.total?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Payment Method */}
            <div className="payment-section">
              <label className="payment-label" id="payment-method-label">{isSell ? t('payoutMethod') : t('payUsing')}</label>

              <div className="dropdown-container" ref={paymentDropdownRef}>
                <button
                  className="payment-selector"
                  onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                  aria-labelledby="payment-method-label"
                  aria-expanded={showPaymentDropdown}
                  aria-haspopup="listbox"
                >
                  <div className="payment-left">
                    <div className="payment-icon" aria-hidden="true">
                      {(() => {
                        const nameOrId = (selectedPaymentMethod?.name || selectedPaymentMethod?.id || selectedPaymentMethod?.code || '').toLowerCase();
                        if (nameOrId.includes('bank') || nameOrId.includes('ach') || nameOrId.includes('sepa') || nameOrId.includes('wire')) return '🏦';
                        if (nameOrId.includes('upi')) return 'UPI';
                        return '💳';
                      })()}
                    </div>
                    <div className="payment-info">
                      <span className="payment-name">
                        {selectedPaymentMethod?.name || t('selectPaymentMethod')}
                      </span>
                      {selectedPaymentMethod?.recommended && (
                        <span className="payment-recommended">{t('recommended')}</span>
                      )}
                    </div>
                  </div>
                  <span className="payment-dropdown-icon" aria-hidden="true">▼</span>
                </button>

                {showPaymentDropdown && (
                  <div className="payment-dropdown-menu" role="listbox" aria-label="Payment methods">
                    {filteredPaymentMethods.map((method) => {
                      const methodId = method.id || method.code;
                      return (
                        <button
                          key={methodId}
                          className={`payment-option ${methodId === paymentMethod ? 'selected' : ''}`}
                          onClick={() => {
                            paymentMethodManuallySelected.current = true;
                            setPaymentMethod(methodId);
                            setShowPaymentDropdown(false);
                          }}
                          role="option"
                          aria-selected={methodId === paymentMethod}
                        >
                          <div className="payment-left">
                            <div className="payment-icon" aria-hidden="true">
                              {(() => {
                                const nameOrId = (method.name || method.id || method.code || '').toLowerCase();
                                if (nameOrId.includes('bank') || nameOrId.includes('ach') || nameOrId.includes('sepa') || nameOrId.includes('wire')) return '🏦';
                                if (nameOrId.includes('upi')) return 'UPI';
                                return '💳';
                              })()}
                            </div>
                            <div className="payment-info">
                              <span className="payment-name">{method.name || methodId}</span>
                              {method.recommended && (
                                <span className="payment-recommended">Recommended</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Wallet Address */}
            <div className="wallet-section">
              <label htmlFor="wallet-address" className="input-label">
                {t('walletAddress')}
                {isWalletLocked && <span className="locked-badge">Locked</span>}
              </label>
              <div className="wallet-input-container">
                <input
                  id="wallet-address"
                  type="text"
                  className={`wallet-input ${walletError ? 'error' : ''} ${isWalletLocked ? 'locked' : ''}`}
                  value={walletAddress}
                  onChange={(e) => {
                    if (isWalletLocked) return;
                    setWalletAddress(e.target.value);
                    setWalletError(null);
                    validateWallet(e.target.value);
                  }}
                  placeholder={!walletAddress ? t('connectWalletToContinue') : ''}
                  disabled={isWalletLocked}
                  readOnly={isWalletLocked}
                  aria-describedby={walletError ? 'wallet-error' : undefined}
                  aria-invalid={!!walletError}
                  aria-required={true}
                />
                {walletError && <div id="wallet-error" className="wallet-error" role="alert">{walletError}</div>}
              </div>
            </div>

            {/* Wallet Purchase Status Banner */}
            {isWalletMode && purchaseStatus && (
              <div className={`purchase-status-banner status-${purchaseStatus.status?.toLowerCase()}`} role="status">
                <div className="purchase-status-icon">
                  {purchaseStatus.status === 'COMPLETED' ? '✓' :
                    purchaseStatus.status === 'FAILED' ? '✕' :
                      purchaseStatus.status === 'PROCESSING' ? '⟳' :
                        purchaseStatus.status === 'AWAITING_PAYMENT' ? '◷' : '•'}
                </div>
                <div className="purchase-status-text">
                  <span className="purchase-status-label">
                    {purchaseStatus.status === 'INITIATED' && 'Purchase Initiated'}
                    {purchaseStatus.status === 'AWAITING_PAYMENT' && 'Awaiting Payment'}
                    {purchaseStatus.status === 'PROCESSING' && 'Processing...'}
                    {purchaseStatus.status === 'COMPLETED' && 'Purchase Complete!'}
                    {purchaseStatus.status === 'FAILED' && 'Purchase Failed'}
                    {purchaseStatus.status === 'EXPIRED' && 'Purchase Expired'}
                    {purchaseStatus.status === 'CANCELLED' && 'Purchase Cancelled'}
                  </span>
                  {purchaseStatus.txHash && (
                    <span className="purchase-tx-hash">
                      Tx: {purchaseStatus.txHash.slice(0, 10)}...{purchaseStatus.txHash.slice(-6)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Payment Success Banner (normal mode) */}
            {paymentComplete?.status === 'COMPLETED' && (
              <div className="bc-success-banner" role="status">
                <div className="bc-success-banner-content">
                  <div className="bc-success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="9,12 11,14 15,10" />
                    </svg>
                  </div>
                  <div className="bc-success-info">
                    <span className="bc-success-label">{t('paymentCompleted')}</span>
                    {paymentComplete.txHash && (
                      <span className="bc-success-tx">
                        Tx: {paymentComplete.txHash.slice(0, 10)}...{paymentComplete.txHash.slice(-6)}
                      </span>
                    )}
                    {config.successUrl && <span className="bc-success-redirect">{t('redirecting')}</span>}
                  </div>
                </div>
                <div className="bc-success-actions">
                  {config.successUrl && (
                    <button
                      className="bc-success-return"
                      onClick={() => {
                        const url = new URL(config.successUrl);
                        if (paymentComplete.orderId) url.searchParams.set('orderId', paymentComplete.orderId);
                        if (paymentComplete.txHash) url.searchParams.set('txHash', paymentComplete.txHash);
                        if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
                        window.location.href = url.toString();
                      }}
                    >
                      {t('return')}
                    </button>
                  )}
                  <button className="bc-success-dismiss" onClick={handleDismissCompletion} aria-label="Dismiss">
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <label className="disclaimer-container">
              <input
                type="checkbox"
                className="disclaimer-checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              />
              <span className="disclaimer-text">
                {t('disclaimerPart1')}
                {isSell ? t('selling') : t('purchasing')}
                {t('disclaimerPart2')}
              </span>
            </label>

            {/* Buy / Sell Button */}
            <button
              className={`buy-button ${loading ? 'loading' : ''}`}
              onClick={handleBuy}
              disabled={loading || !selectedProvider || quotesLoading || !!amountError || !disclaimerAccepted || !walletAddress || !!walletError || awaitingPayment.active || paymentComplete?.status === 'COMPLETED'}
              aria-busy={loading}
            >
              {!walletAddress
                ? t('connectWalletToContinue')
                : walletError
                  ? t('enterValidWalletAddress')
                  : !disclaimerAccepted
                    ? t('acceptDisclaimerToContinue')
                    : isSell
                      ? t('sellCurrency', { crypto: cryptoCurrency })
                      : t('buyCurrency', { crypto: cryptoCurrency })}
            </button>
          </div>

          {/* Footer removed */}

          {/* Provider Selection Modal — Redesigned Modern Web3 Sheet */}
          {showProvidersModal && (
            <div className="providers-modal-overlay" onClick={() => setShowProvidersModal(false)}>
              <div className="providers-modal" onClick={(e) => e.stopPropagation()}>
                {/* Drag handle */}
                <div className="providers-modal-drag-handle"></div>

                <div className="providers-modal-header">
                  <div className="providers-modal-header-left">
                    <div className="providers-modal-badge">
                      <span className="live-dot"></span>
                      {t('realtimeComparison')}
                    </div>
                    <h3 className="providers-modal-title">{t('selectRouteProvider')}</h3>
                    <p className="providers-modal-subtitle">
                      {t('routesFound', {
                        count: providerQuotes.length,
                        amount: fiatAmount,
                        from: isSell ? cryptoCurrency : fiatCurrency,
                        to: isSell ? fiatCurrency : cryptoCurrency,
                      })}
                    </p>
                  </div>
                  <button
                    className="providers-modal-close"
                    onClick={() => setShowProvidersModal(false)}
                    aria-label="Close"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div className="providers-modal-list">
                  {providerQuotes.map((quote) => {
                    const isSelected = selectedProvider?.provider === quote.provider;
                    const providerLogoUrl = providerIcons[quote.provider] || PROVIDER_LOGOS[quote.provider];
                    const currentPayout = isSell ? (quote.fiatAmount || quote.payoutAmount || 0) : (quote.cryptoAmount || quote.payoutAmount || 0);
                    const bestQuote = providerQuotes.find(q => q.isBestPrice) || providerQuotes[0];
                    const bestPayoutVal = isSell ? (bestQuote?.fiatAmount || bestQuote?.payoutAmount || 0) : (bestQuote?.cryptoAmount || bestQuote?.payoutAmount || 0);
                    const diff = bestPayoutVal > 0 ? (currentPayout - bestPayoutVal) : 0;
                    const isBest = quote.isBestPrice || Math.abs(diff) < 0.0001;

                    return (
                      <div
                        key={quote.provider}
                        className={`providers-modal-card ${isSelected ? 'selected' : ''} ${isBest ? 'is-best' : ''}`}
                        onClick={() => {
                          handleProviderSelect(quote);
                          setShowProvidersModal(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleProviderSelect(quote);
                            setShowProvidersModal(false);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isSelected}
                      >
                        {/* Top Row: Provider Brand, Name & Payout */}
                        <div className="providers-card-top">
                          <div className="providers-card-brand">
                            <div className="provider-card-logo">
                              {providerLogoUrl ? (
                                <img
                                  src={providerLogoUrl}
                                  alt=""
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                  }}
                                />
                              ) : null}
                              <span style={{ display: providerLogoUrl ? 'none' : 'flex' }}>
                                {quote.provider.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="providers-brand-text">
                              <div className="providers-name-row">
                                <span className="providers-card-name">
                                  {quote.providerDisplayName || quote.provider.charAt(0).toUpperCase() + quote.provider.slice(1)}
                                </span>
                                {isBest && (
                                  <span className="provider-pill-badge best">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                    </svg>
                                    {t('bestRate')}
                                  </span>
                                )}
                                {quote.estimatedDeliveryMinutes && (
                                  <span className="provider-pill-badge speed">
                                    ⚡ ~{quote.estimatedDeliveryMinutes}m
                                  </span>
                                )}
                              </div>
                              <span className="providers-card-rate-sub">
                                {quote.cryptoAmount > 0
                                  ? `1 ${cryptoCurrency} ≈ ${(quote.fiatAmount / quote.cryptoAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${fiatCurrency}`
                                  : ''}
                              </span>
                            </div>
                          </div>

                          <div className="providers-card-payout">
                            <div className="providers-payout-value">
                              {isSell
                                ? `${Number(quote.fiatAmount || quote.payoutAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${fiatCurrency}`
                                : `${Number(quote.cryptoAmount || quote.payoutAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} ${cryptoCurrency}`}
                            </div>
                            {isBest ? (
                              <span className="payout-advantage-tag best">🏆 Best Payout</span>
                            ) : (
                              <span className="payout-advantage-tag diff">
                                {diff < 0 ? `-${Math.abs(diff).toFixed(2)} ${isSell ? fiatCurrency : cryptoCurrency}` : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Bottom Row: Fees, Payment Methods & Selection Indicator */}
                        <div className="providers-card-bottom">
                          <div className="providers-card-meta-tags">
                            <span className="meta-tag fee">
                              {quote.fees?.total > 0
                                ? `Fee: ${quote.fees.total.toFixed(2)} ${fiatCurrency}`
                                : 'Zero fee'}
                            </span>
                            {quote.availablePaymentMethods?.slice(0, 2).map((m, idx) => (
                              <span key={idx} className="meta-tag method">
                                {typeof m === 'object' ? m.name : m}
                              </span>
                            ))}
                          </div>

                          <div className={`providers-selection-indicator ${isSelected ? 'active' : ''}`}>
                            {isSelected ? (
                              <span className="selected-pill">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                Selected
                              </span>
                            ) : (
                              <span className="select-action-label">Select</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="providers-modal-footer">
                  <span className="footer-tip-icon">💡</span>
                  <span>Quotes update in real-time. Best price is pre-selected for maximum payout.</span>
                </div>
              </div>
            </div>
          )}

          {/* Awaiting Payment Overlay (normal mode only) */}
          {awaitingPayment.active && !isWalletMode && !isLocked && (
            <div className="bc-awaiting-overlay">
              {/* Close button */}
              <button
                className="bc-awaiting-close"
                onClick={handleDismissAwaiting}
                aria-label="Close and return to form"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              {awaitingPayment.embedAs === 'iframe' && awaitingPayment.widgetUrl ? (
                /* In-page iframe (Guardarian & future iframe providers).
                   `allow` grants the permissions required for KYC selfie +
                   3-D-Secure flows; do NOT add a `sandbox` attribute, it
                   silently strips camera/microphone access. */
                <div className="bc-awaiting-iframe-wrap">
                  <iframe
                    src={awaitingPayment.widgetUrl}
                    title={`${awaitingPayment.providerName} checkout`}
                    allow="camera; microphone; fullscreen; payment; accelerometer; autoplay; gyroscope"
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '600px',
                      border: 0,
                    }}
                  />
                </div>
              ) : (
                <div className="bc-awaiting-content">
                  {/* Pulsing animation */}
                  <div className="bc-awaiting-pulse-container">
                    <div className="bc-awaiting-pulse"></div>
                    <div className="bc-awaiting-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    </div>
                  </div>

                  <h3 className="bc-awaiting-title">Complete Your Payment</h3>

                  <p className="bc-awaiting-message">
                    Please complete your payment on the <strong>{awaitingPayment.providerName}</strong> page.
                  </p>

                  {awaitingPayment.popupBlocked && (
                    <div className="bc-awaiting-blocked">
                      <span>Popup was blocked. Click below to open the payment page:</span>
                    </div>
                  )}

                  {awaitingPayment.widgetUrl && (
                    <a
                      href={awaitingPayment.widgetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bc-awaiting-link"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                        <polyline points="15,3 21,3 21,9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      {t('openPaymentPage', { provider: awaitingPayment.providerName })}
                    </a>
                  )}

                  <div className="bc-awaiting-waiting">
                    <div className="bc-awaiting-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span>{t('waitingConfirmation')}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BuyCrypto;
